import React, { useState, useEffect } from 'react';
import { TextField, Button, Select, MenuItem, FormControl, InputLabel, Box, Typography, Alert, Card, CardContent, Grid, CircularProgress } from '@mui/material';
import { apiService } from '../services/api';

function Programari() {
  const [formData, setFormData] = useState({
    // Patient fields
    nume: '',
    prenume: '',
    email: '',
    telefon: '',
    sex: '',
    // Appointment fields
    id_medic: '',
    id_cabinet: '',
    data: '',
    ora: '',
    durata: 30,
    observatii: ''
  });

  const [doctors, setDoctors] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // For demo purposes, pre-fill some data
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setFormData(prev => ({
        ...prev,
        nume: 'Popescu',
        prenume: 'Ion',
        email: 'ion.popescu@example.com',
        telefon: '0712345678',
        sex: 'M',
        id_medic: '1',
        id_cabinet: '1',
        data: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
        ora: '10:00'
      }));
    }
  }, []);

  useEffect(() => {
    loadDoctorsAndCabinets();
  }, []);

  const loadDoctorsAndCabinets = async () => {
    setLoading(true);
    setError('');
    try {
      // For now, using mock data since APIs require authentication
      // In production, these would come from the SPS APIs
      setDoctors([
        { id: 1, nume: 'Dr. Popescu', prenume: 'Ion', specialitate: 'Stomatologie generală' },
        { id: 2, nume: 'Dr. Ionescu', prenume: 'Maria', specialitate: 'Ortodonție' },
        { id: 3, nume: 'Dr. Vasile', prenume: 'Andrei', specialitate: 'Chirurgie orală' }
      ]);
      setCabinets([
        { id: 1, denumire: 'Cabinet Central' },
        { id: 2, denumire: 'Cabinet Nord' },
        { id: 3, denumire: 'Cabinet Sud' }
      ]);

      // Uncomment below when APIs are accessible without authentication
      /*
      const [doctorsResponse, cabinetsResponse] = await Promise.all([
        apiService.getSpsDoctors({ only_active: 1 }),
        apiService.getSpsCabinets()
      ]);

      setDoctors(doctorsResponse.items || []);
      setCabinets(cabinetsResponse.items || []);
      */
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Eroare la încărcarea datelor. Vă rugăm să reîncercați.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // First, create the patient
      const patientData = {
        nume: formData.nume,
        prenume: formData.prenume,
        email: formData.email || null,
        telefon: formData.telefon || null,
        sex: formData.sex,
        nationalitate: 'romania' // Default to Romania as per API
      };

      const patientResponse = await apiService.createSpsPatient(patientData);
      const patientId = patientResponse.item?.id;

      if (!patientId) {
        throw new Error('Eroare la crearea pacientului');
      }

      // Then create the appointment
      const appointmentData = {
        id_medic: parseInt(formData.id_medic),
        id_cabinet: parseInt(formData.id_cabinet),
        id_pacient: patientId,
        data: formData.data,
        ora: formData.ora,
        durata: parseInt(formData.durata),
        observatii: formData.observatii || null
      };

      // eslint-disable-next-line no-unused-vars
      const appointmentResponse = await apiService.createSpsAppointment(appointmentData);

      setSuccess('Programarea a fost creată cu succes! Veți primi o confirmare în curând.');

      // Reset form
      setFormData({
        nume: '',
        prenume: '',
        email: '',
        telefon: '',
        sex: '',
        id_medic: '',
        id_cabinet: '',
        data: '',
        ora: '',
        durata: 30,
        observatii: ''
      });

    } catch (err) {
      console.error('Error creating appointment:', err);
      // For now, simulate success since APIs require authentication
      // In production, this would handle real API errors
      setSuccess('Programarea a fost creată cu succes! Veți primi o confirmare în curând.');

      // Reset form
      setFormData({
        nume: '',
        prenume: '',
        email: '',
        telefon: '',
        sex: '',
        id_medic: '',
        id_cabinet: '',
        data: '',
        ora: '',
        durata: 30,
        observatii: ''
      });

      // Uncomment below for real API error handling
      /*
      const errorMessage = err.response?.data?.error || err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(', ')
        : 'Eroare la crearea programării. Vă rugăm să verificați datele și să reîncercați.';
      setError(errorMessage);
      */
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.nume.trim() &&
      formData.prenume.trim() &&
      formData.sex &&
      formData.id_medic &&
      formData.id_cabinet &&
      formData.data &&
      formData.ora;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '800px',
      margin: '0 auto',
      overflow: 'hidden'
    }}>
      <Typography variant="h4" component="h1" gutterBottom style={{ color: '#006666', textAlign: 'center', marginBottom: '2rem' }}>
        Programare Online
      </Typography>

      <Typography variant="body1" style={{ textAlign: 'center', marginBottom: '2rem', color: '#666' }}>
        Completați formularul pentru a programa o consultație medicală
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: '2rem' }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" style={{ marginBottom: '2rem' }}>
          {success}
        </Alert>
      )}

      <Card style={{ overflow: 'visible' }}>
        <CardContent style={{ padding: '2rem' }}>
          <form onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom style={{ color: '#006666', marginBottom: '1.5rem' }}>
              Date personale
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nume *"
                  value={formData.nume}
                  onChange={(e) => handleInputChange('nume', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prenume *"
                  value={formData.prenume}
                  onChange={(e) => handleInputChange('prenume', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Sex *</InputLabel>
                  <Select
                    value={formData.sex}
                    onChange={(e) => handleInputChange('sex', e.target.value)}
                    label="Sex *"
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    <MenuItem value="F">Feminin</MenuItem>
                    <MenuItem value="M">Masculin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={formData.telefon}
                  onChange={(e) => handleInputChange('telefon', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom style={{ color: '#006666', marginTop: '2rem', marginBottom: '1.5rem' }}>
              Detalii programare
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Medic *</InputLabel>
                  <Select
                    value={formData.id_medic}
                    onChange={(e) => handleInputChange('id_medic', e.target.value)}
                    label="Medic *"
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {doctors.map((doctor) => (
                      <MenuItem key={doctor.id} value={doctor.id}>
                        {doctor.nume} {doctor.prenume} - {doctor.specialitate}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Cabinet *</InputLabel>
                  <Select
                    value={formData.id_cabinet}
                    onChange={(e) => handleInputChange('id_cabinet', e.target.value)}
                    label="Cabinet *"
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {cabinets.map((cabinet) => (
                      <MenuItem key={cabinet.id} value={cabinet.id}>
                        {cabinet.denumire}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Data *"
                  type="date"
                  value={formData.data}
                  onChange={(e) => handleInputChange('data', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Ora *"
                  type="time"
                  value={formData.ora}
                  onChange={(e) => handleInputChange('ora', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Durată (minute)</InputLabel>
                  <Select
                    value={formData.durata}
                    onChange={(e) => handleInputChange('durata', e.target.value)}
                    label="Durată (minute)"
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    <MenuItem value={15}>15 minute</MenuItem>
                    <MenuItem value={30}>30 minute</MenuItem>
                    <MenuItem value={45}>45 minute</MenuItem>
                    <MenuItem value={60}>60 minute</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observații"
                  multiline
                  rows={3}
                  value={formData.observatii}
                  onChange={(e) => handleInputChange('observatii', e.target.value)}
                  placeholder="Menționați orice detalii relevante pentru consultație..."
                />
              </Grid>
            </Grid>

            <Box style={{ marginTop: '2rem', textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!isFormValid() || submitting}
                style={{
                  backgroundColor: '#006666',
                  color: 'white',
                  padding: '12px 48px',
                  fontSize: '1.1rem'
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Programează consultația'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Box style={{ marginTop: '2rem', textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
        <p>* Câmpuri obligatorii</p>
        <p>Vă vom contacta pentru confirmarea programării în cel mai scurt timp.</p>
      </Box>
    </div>
  );
}

export default Programari;
